<template>
    <div class="min-h-screen">
        <div class="w-full">
            <div class="grid-cols-2 gap-8 md:grid">
                <!-- Left Column - Overdue Tasks -->
                <div class="col-span-1" v-if="delayedEvents.length > 0">
                    <card-basic>
                        <template v-slot:body>
                            <div class="flex justify-between items-center mb-5">
                                <div>
                                    <h5 class="font-semibold text-gray-900">
                                        <mf-translate domainkey="crm:delayedActions"></mf-translate>
                                    </h5>
                                    <h6 class="text-gray-600">{{ totalDelayed }} total</h6>
                                </div>
                                <surrender-overdue-modal
                                    :overdue-count="totalDelayed"
                                    :route="surrenderRoute"
                                    class="text-myflowPurple hover:text-myflowPurple-700 font-medium"
                                />
                            </div>

                            <div class="space-y-2">
                                <card-sub
                                    v-for="(event, index) in delayedEvents"
                                    :key="event.id"
                                    class="relative pl-3 transition-all duration-200 hover:pl-4"
                                    :class="getPriorityBorderClass(event)"
                                >
                                    <a v-if="event.lead"
                                       :href="getLeadUrl(event.lead)"
                                       class="block">
                                        <span class="float-right">
                                            <template v-if="event.prio > 0">
                                                <i v-for="i in event.prio" :key="i"
                                                   class="fa-solid fa-exclamation text-myflowRed-800 animate-pulse"
                                                   style="margin-left:2px"></i>
                                            </template>
                                        </span>
                                        <span class="text-gray-900">
                                            <b class="text-myflowPurple mf-link">{{ event.lead_rel?.name || '' }}</b><br>
                                            <span v-if="event.lead_rel?.company">{{ event.lead_rel.company }}<br></span>
                                            <i>{{ event.action || 'No action' }}</i><br>
                                            <span class="text-red-600 text-sm font-medium">{{ formatDate(event) }}</span>
                                        </span>
                                    </a>
                                    <div v-else class="py-2 text-gray-900">
                                        <span class="float-right">
                                            <template v-if="event.prio > 0">
                                                <i v-for="i in event.prio" :key="i"
                                                   class="fa-solid fa-exclamation text-myflowRed-800 animate-pulse"
                                                   style="margin-left:2px"></i>
                                            </template>
                                        </span>
                                        <i>{{ event.action || 'No action' }}</i><br>
                                        <span class="text-red-600 text-sm font-medium">{{ formatDate(event) }}</span>
                                    </div>
                                </card-sub>
                            </div>

                            <div v-if="delayedPagination.total" class="mt-4 flex flex-col gap-3 text-sm text-gray-600 sm:flex-row sm:items-center sm:justify-between">
                                <span>
                                    Showing {{ delayedPagination.from }}-{{ delayedPagination.to }} of {{ delayedPagination.total }}
                                </span>
                                <div class="flex items-center gap-4">
                                    <a v-if="delayedPagination.prev_page_url" :href="delayedPagination.prev_page_url" class="mf-link">Previous</a>
                                    <a v-if="delayedPagination.next_page_url" :href="delayedPagination.next_page_url" class="mf-link">Next</a>
                                </div>
                            </div>
                        </template>
                    </card-basic>
                </div>

                <!-- Right Column - Upcoming Tasks -->
                <div :class="delayedEvents.length > 0 ? 'col-span-1' : 'col-span-2'">
                    <card-basic>
                        <template v-slot:body>
                            <h5 class="font-semibold text-gray-900 mb-5">
                                <mf-translate domainkey="crm:myFollowups"></mf-translate>
                            </h5>

                            <!-- Today's Stats -->
                            <div v-if="todaysEvents.length > 0" class="mb-6 p-4 bg-gradient-to-r from-purple-50 to-indigo-50 rounded-lg border border-purple-100">
                                <div class="flex items-center justify-between mb-3">
                                    <h6 class="font-semibold text-gray-800">
                                        <mf-translate domainkey="crm:todaysActivities"></mf-translate>
                                    </h6>
                                    <div v-if="streak > 0" class="flex items-center">
                                        <i class="fa-solid fa-fire"
                                           :class="getStreakColor(streak)"
                                           :style="streak > 7 ? 'filter: drop-shadow(0 0 3px rgba(251, 146, 60, 0.5))' : ''"></i>
                                        <span class="ml-1 font-bold" :class="getStreakColor(streak)">{{ streak }}</span>
                                        <span class="text-sm text-gray-600 ml-1">{{ streak === 1 ? 'day' : 'days' }}</span>
                                    </div>
                                </div>

                                <!-- Progress Bar -->
                                <div class="mb-2">
                                    <div class="flex justify-between text-sm text-gray-600 mb-1">
                                        <span>{{ completedTasks }} <mf-translate domainkey="shared:of"></mf-translate> {{ todaysEvents.length }} completed</span>
                                        <span>{{ Math.round((completedTasks / todaysEvents.length) * 100) }}%</span>
                                    </div>
                                    <div class="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                                        <div class="bg-gradient-to-r from-myflowGreen to-green-500 h-2 rounded-full transition-all duration-500"
                                             :style="`width: ${(completedTasks / todaysEvents.length) * 100}%`"></div>
                                    </div>
                                </div>
                            </div>

                            <!-- Tasks by Date -->
                            <div v-for="(dayEvents, date) in groupedEvents" :key="date">
                                <div class="mb-3">
                                    <div class="flex items-center justify-between mb-2">
                                        <div>
                                            <h6 :class="isToday(date) ? 'font-bold text-myflowPurple text-lg' : 'font-medium text-gray-700'">
                                                {{ formatDateHeader(date) }}
                                            </h6>
                                            <span class="text-xs text-gray-500">{{ getFullDate(date) }}</span>
                                        </div>
                                        <span v-if="isToday(date)" class="text-xs bg-gradient-to-r from-myflowPurple to-indigo-600 text-white px-3 py-1 rounded-full font-medium shadow-sm">
                                            TODAY
                                        </span>
                                    </div>

                                    <div v-if="dayEvents.length > 0" :class="isToday(date) ? 'bg-purple-50/30 -mx-4 px-4 py-2 rounded-lg' : ''">
                                        <div v-for="(event, eventIndex) in dayEvents" :key="event.id"
                                             class="relative pl-3 transition-all duration-200 hover:pl-4"
                                             :class="getPriorityBorderClass(event)">
                                            <a v-if="event.lead"
                                               :href="getLeadUrl(event.lead)"
                                               class="block py-2">
                                                <span class="float-right">
                                                    <i v-if="event.done" class="fa-solid fa-square-check text-myflowGreen transition-transform hover:scale-110"></i>
                                                    <template v-else-if="event.prio > 0">
                                                        <i v-for="i in event.prio" :key="i"
                                                           class="fa-solid fa-exclamation text-myflowRed-800"
                                                           style="margin-left:2px"></i>
                                                    </template>
                                                </span>
                                                <span class="text-gray-900">
                                                    <b class="text-myflowPurple mf-link" :class="event.done ? 'line-through opacity-60' : ''">{{ event.lead_rel?.name || '' }}</b><br>
                                                    <span v-if="event.lead_rel?.company" :class="event.done ? 'line-through opacity-60' : ''">{{ event.lead_rel.company }}<br></span>
                                                    <i :class="event.done ? 'line-through opacity-60' : ''">{{ event.action || '' }}</i>
                                                </span>
                                            </a>
                                            <div v-else class="py-2 text-gray-900">
                                                <span class="float-right">
                                                    <i v-if="event.done" class="fa-solid fa-square-check text-myflowGreen transition-transform hover:scale-110"></i>
                                                    <template v-else-if="event.prio > 0">
                                                        <i v-for="i in event.prio" :key="i"
                                                           class="fa-solid fa-exclamation text-myflowRed-800"
                                                           style="margin-left:2px"></i>
                                                    </template>
                                                </span>
                                                <span :class="event.done ? 'line-through opacity-60' : ''">
                                                    <i>{{ event.action || 'No action' }}</i>
                                                </span>
                                            </div>
                                            <hr v-if="eventIndex < dayEvents.length - 1" class="mt-2 mb-2">
                                        </div>
                                    </div>
                                    <div v-else class="text-gray-400 italic py-2">
                                        No tasks scheduled
                                    </div>
                                </div>
                                <hr class="mt-4 mb-4">
                            </div>
                        </template>
                    </card-basic>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, computed } from 'vue';

const props = defineProps({
    initialDelayedEvents: {
        type: Array,
        default: () => []
    },
    initialDelayedPagination: {
        type: Object,
        default: () => ({})
    },
    initialEvents: {
        type: Array,
        default: () => []
    },
    surrenderRoute: {
        type: String,
        required: true
    },
    userId: {
        type: Number,
        required: true
    },
    userName: {
        type: String,
        default: ''
    },
    streak: {
        type: Number,
        default: 0
    },
    todaysPending: {
        type: Number,
        default: 0
    },
    todaysCompleted: {
        type: Number,
        default: 0
    }
});

const delayedEvents = ref(props.initialDelayedEvents);
const delayedPagination = ref(props.initialDelayedPagination || {});
const totalDelayed = computed(() => {
    const metadata = delayedPagination.value || {};
    return metadata.total ?? delayedEvents.value.length;
});
const events = ref(props.initialEvents);
const streak = ref(props.streak);

const todaysEvents = computed(() => {
    return events.value.filter(e => e.is_today);
});

const completedTasks = computed(() => props.todaysCompleted);

const groupedEvents = computed(() => {
    const grouped = {};
    const dates = [...new Set(events.value.map(e => e.date))];

    dates.forEach(date => {
        grouped[date] = events.value.filter(e => e.date === date);
    });

    return grouped;
});

const formatDate = (event) => {
    if (event.days_ago_text) return event.days_ago_text;
    return event.date_display || event.formatted_date || event.date;
};

const formatDateHeader = (date) => {
    const eventsForDate = events.value.filter(e => e.date === date);
    if (eventsForDate.length > 0) {
        const firstEvent = eventsForDate[0];
        if (firstEvent.is_today) return 'Today';
        if (firstEvent.is_tomorrow) return 'Tomorrow';
        return firstEvent.day_name || date;
    }
    return date;
};

const getFullDate = (date) => {
    const eventsForDate = events.value.filter(e => e.date === date);
    if (eventsForDate.length > 0) {
        const firstEvent = eventsForDate[0];
        return firstEvent.full_date || firstEvent.formatted_date || date;
    }
    return date;
};

const isToday = (date) => {
    const eventsForDate = events.value.filter(e => e.date === date);
    return eventsForDate.length > 0 && eventsForDate[0].is_today;
};

const getLeadUrl = (leadId) => {
    if (!leadId) return '#';
    return `/crm/leads/${leadId}`;
};

const getStreakColor = (streak) => {
    if (streak >= 14) return 'text-orange-600';
    if (streak >= 7) return 'text-orange-500';
    if (streak >= 3) return 'text-orange-400';
    return 'text-gray-500';
};

const getPriorityBorderClass = (event) => {
    if (event.done) return 'border-l-2 border-green-400';
    if (event.prio >= 3) return 'border-l-4 border-red-500';
    if (event.prio >= 2) return 'border-l-3 border-red-400';
    if (event.prio >= 1) return 'border-l-2 border-orange-400';
    return 'border-l-2 border-gray-300';
};
</script>

<style scoped>
@keyframes pulse {
    0%, 100% {
        opacity: 1;
    }
    50% {
        opacity: 0.5;
    }
}

.animate-pulse {
    animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

.border-l-2 {
    border-left-width: 2px;
}

.border-l-3 {
    border-left-width: 3px;
}

.border-l-4 {
    border-left-width: 4px;
}
</style>
